/**
 * Transition « vol des drapeaux » (signature du legacy) au passage en phase à
 * élimination directe (mode Simulation) : les groupes s'effacent avec une
 * animation, et les drapeaux des qualifiés glissent depuis les groupes vers
 * leurs slots des 16es de finale, puis le tableau apparaît.
 *
 * Robuste au reflow : les clones volants sont en position:fixed (coordonnées
 * écran), insensibles au recalcul de mise en page. Annulable via `shouldAbort`
 * si un nouveau scénario démarre pendant le vol (>1 s).
 */
import type { OfficialState } from "../app/officialStore.ts";
import { renderBracket, fitBracket } from "./components/bracketView.ts";

// NB : la transition se joue MÊME sous prefers-reduced-motion. Comme le replay
// forcé, c'est un contenu cinématique explicitement déclenché par l'utilisateur
// (et nos règles CSS « reduced-motion » ne touchent ni .gc ni .fly-flag).

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
const nextFrame = (): Promise<void> =>
  new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

const FLIGHT_MS = 700;
const STAGGER_MS = 14;

/** Teams entering the round of 32 (M73..M88), in node/slot order. */
function r32Teams(official: OfficialState): Array<{ team: string; node: string; slot: 0 | 1 }> {
  const out: Array<{ team: string; node: string; slot: 0 | 1 }> = [];
  for (let i = 73; i <= 88; i++) {
    const node = official.bracket[`M${i}`];
    if (node?.a) out.push({ team: node.a, node: `M${i}`, slot: 0 });
    if (node?.b) out.push({ team: node.b, node: `M${i}`, slot: 1 });
  }
  return out;
}

function revealInstantly(groupsEl: HTMLElement, bracketEl: HTMLElement, official: OfficialState): void {
  groupsEl.classList.add("hide");
  bracketEl.classList.remove("hide");
  renderBracket(bracketEl, official, null);
  fitBracket(bracketEl);
}

/**
 * Joue la transition. `groupsEl` contient les cartes de groupes animées en
 * replay ; `bracketEl` reçoit le tableau. Résout quand l'animation est finie.
 */
export async function koFlagFlight(
  groupsEl: HTMLElement,
  bracketEl: HTMLElement,
  official: OfficialState,
  shouldAbort: () => boolean = () => false,
): Promise<void> {
  // Abandon propre si un nouveau run démarre pendant le vol : on ne touche plus
  // au DOM (qui appartient désormais au nouveau run) et on retire les clones.
  const removeClones = (): void => {
    document.querySelectorAll<HTMLElement>(".fly-flag").forEach((c) => c.remove());
  };
  // 1. Positions SOURCE : drapeaux des qualifiés dans les groupes.
  const teams = r32Teams(official);
  const sources = new Map<string, { rect: DOMRect; el: HTMLElement }>();
  for (const { team } of teams) {
    const f = groupsEl.querySelector<HTMLElement>(`.gr[data-t="${CSS.escape(team)}"] .f`);
    if (f) sources.set(team, { rect: f.getBoundingClientRect(), el: f });
  }
  if (sources.size === 0) {
    revealInstantly(groupsEl, bracketEl, official);
    return;
  }

  // 2. Les groupes s'effacent (fondu + repli, en cascade).
  const cards = [...groupsEl.querySelectorAll<HTMLElement>(".gc")];
  cards.forEach((c, i) => {
    c.style.transition = `opacity .42s ease ${i * 18}ms, transform .42s ease ${i * 18}ms`;
  });
  await nextFrame();
  cards.forEach((c) => {
    c.style.opacity = "0";
    c.style.transform = "scale(.9) translateY(-10px)";
  });
  await sleep(360 + cards.length * 18);
  if (shouldAbort()) {
    removeClones();
    return;
  }
  groupsEl.classList.add("hide");

  // 3. Le tableau est rendu et placé à sa position finale (groupes retirés du flux).
  bracketEl.classList.remove("hide");
  renderBracket(bracketEl, official, null);
  fitBracket(bracketEl);
  await nextFrame();

  // 4. Positions CIBLE : drapeaux des slots 16es (après mise en page définitive).
  const targets = new Map<string, DOMRect>();
  for (let i = 73; i <= 88; i++) {
    const card = bracketEl.querySelector<HTMLElement>(`.m[data-m="M${i}"]`);
    if (!card) continue;
    const rows = card.querySelectorAll<HTMLElement>(".tr");
    const node = official.bracket[`M${i}`];
    [node?.a, node?.b].forEach((t, idx) => {
      const f = rows[idx]?.querySelector<HTMLElement>(".f");
      if (t && f) targets.set(t, f.getBoundingClientRect());
    });
  }

  // 5. Le tableau apparaît en fondu ; les drapeaux clones volent source → cible.
  bracketEl.style.opacity = "0";
  void bracketEl.offsetWidth;
  bracketEl.style.transition = "opacity .5s ease";
  bracketEl.style.opacity = "1";

  const flights: Array<Promise<void>> = [];
  let k = 0;
  for (const [team, src] of sources) {
    const tgt = targets.get(team);
    if (!tgt) continue;
    flights.push(flyOne(src.el, src.rect, tgt, k++));
  }
  await Promise.all(flights);
  if (shouldAbort()) {
    removeClones();
    return;
  }

  bracketEl.style.transition = "";
  bracketEl.style.opacity = "";
}

function flyOne(srcEl: HTMLElement, src: DOMRect, tgt: DOMRect, idx: number): Promise<void> {
  return new Promise((resolve) => {
    const clone = srcEl.cloneNode(true) as HTMLElement;
    clone.classList.add("fly-flag");
    const s = tgt.width / Math.max(1, src.width);
    Object.assign(clone.style, {
      position: "fixed",
      left: `${src.left}px`,
      top: `${src.top}px`,
      width: `${src.width}px`,
      height: `${src.height}px`,
      margin: "0",
      zIndex: "9999",
      pointerEvents: "none",
      transition: `transform ${FLIGHT_MS}ms cubic-bezier(.45,0,.2,1) ${idx * STAGGER_MS}ms`,
    });
    document.body.appendChild(clone);
    const dx = tgt.left - src.left;
    const dy = tgt.top - src.top;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        clone.style.transform = `translate(${dx}px, ${dy}px) scale(${s})`;
      }),
    );
    window.setTimeout(
      () => {
        clone.remove();
        resolve();
      },
      FLIGHT_MS + idx * STAGGER_MS + 60,
    );
  });
}
